// CardManagement.tsx
import React, { useState, useEffect } from 'react';
import { Student, RFIDCard } from '../../types';
import { cardService } from '../../services/cardService';

const CardManagement: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<number>(0);
  const [newCardNumber, setNewCardNumber] = useState('');
  const [showAddStudentForm, setShowAddStudentForm] = useState(false);
  const [newStudentName, setNewStudentName] = useState('');
  const [newStudentId, setNewStudentId] = useState('');
  const [cardToReportLost, setCardToReportLost] = useState<RFIDCard | null>(null);
  const [showLostCardConfirm, setShowLostCardConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [autoFocusNewCard, setAutoFocusNewCard] = useState(false);
  const [studentCards, setStudentCards] = useState<RFIDCard[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [cardNumberError, setCardNumberError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const userStudents = await cardService.getUserStudents();
      setStudents(userStudents);
      if (userStudents.length > 0) {
        setSelectedStudent(userStudents[0].id);
        await loadStudentCards(userStudents[0].id);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      alert('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const loadStudentCards = async (studentId: number) => {
    try {
      setRefreshing(true);
      const cards = await cardService.getStudentCards(studentId);
      setStudentCards(cards);
    } catch (error) {
      console.error('Failed to load user cards:', error);
      setStudentCards([]);
    } finally {
      setRefreshing(false);
    }
  };

  const handleStudentChange = async (studentId: number) => {
    setSelectedStudent(studentId);
    await loadStudentCards(studentId);
  };

  const getCurrentCard = (): RFIDCard | undefined => {
    if (!selectedStudent) return undefined;
    const student = students.find(s => s.id === selectedStudent);
    if (!student || !student.currentCardId) return undefined;
    return studentCards.find(card => card.id === student.currentCardId);
  };

  const validateCardNumber = (cardNumber: string): boolean => {
    // Remove any spaces or dashes from input
    const cleanCardNumber = cardNumber.replace(/[\s-]/g, '');
    
    // Check if it's exactly 10 digits
    if (!/^\d{10}$/.test(cleanCardNumber)) {
      setCardNumberError('RFID card number must be exactly 10 digits');
      return false;
    }
    
    setCardNumberError('');
    return true;
  };

  const handleCardNumberChange = (value: string) => {
    // Remove any non-digit characters
    const cleanValue = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    if (cleanValue.length <= 10) {
      setNewCardNumber(cleanValue);
      
      // Validate as user types (only show error if they've entered something)
      if (cleanValue.length > 0) {
        validateCardNumber(cleanValue);
      } else {
        setCardNumberError('');
      }
    }
  };

  const refreshCurrentStudent = async () => {
    try {
      const userStudents = await cardService.getUserStudents();
      setStudents(userStudents);
    } catch (error) {
      console.error('Failed to refresh student data:', error);
    }
  };

  const handleAddStudent = async () => {
    if (!newStudentName || !newStudentId) {
      alert('Please enter both user name and ID');
      return;
    }

    try {
      await cardService.addStudent(newStudentName, newStudentId);
      await loadData();
      setNewStudentName('');
      setNewStudentId('');
      setShowAddStudentForm(false);
      alert('User added successfully!');
    } catch (error: any) {
      console.error('Failed to add user:', error);
      alert(error.response?.data?.message || 'Failed to add user');
    }
  };

  const handleAddCard = async () => {
    if (!newCardNumber) {
      alert('Please enter a card number');
      return;
    }

    if (!validateCardNumber(newCardNumber)) {
      return;
    }

    try {
      await cardService.addCard(selectedStudent, newCardNumber);
      await loadStudentCards(selectedStudent);
      await refreshCurrentStudent();
      setNewCardNumber('');
      setAutoFocusNewCard(false);
      alert('Card added successfully!');
    } catch (error: any) {
      console.error('Failed to add card:', error);
      alert(error.response?.data?.message || 'Failed to add card');
    }
  };

  const handleReplaceCard = async (oldCardId: number) => {
    if (!newCardNumber) {
      alert('Please enter a new card number');
      return;
    }

    if (!validateCardNumber(newCardNumber)) {
      return;
    }

    try {
      await cardService.replaceCard(oldCardId, newCardNumber);
      await loadStudentCards(selectedStudent);
      await refreshCurrentStudent();
      setNewCardNumber('');
      setAutoFocusNewCard(false);
      alert('Card replaced successfully!');
    } catch (error: any) {
      console.error('Failed to replace card:', error);
      alert(error.response?.data?.message || 'Failed to replace card');
    }
  };

  const confirmReportLost = (card: RFIDCard) => {
    setCardToReportLost(card);
    setShowLostCardConfirm(true);
  };

  const handleReportLost = async () => {
    if (!cardToReportLost) return;

    try {
      await cardService.reportCardLost(cardToReportLost.id);
      await loadStudentCards(selectedStudent);
      setCardToReportLost(null);
      setShowLostCardConfirm(false);
      setAutoFocusNewCard(true);
      alert('Card reported as lost successfully!');
    } catch (error: any) {
      console.error('Failed to report card as lost:', error);
      alert(error.response?.data?.message || 'Failed to report card as lost');
    }
  };

  const selectedStudentData = students.find(s => s.id === selectedStudent);
  const currentCard = getCurrentCard();
  const hasActiveCards = studentCards.some(card => card.isActive && !card.isLost);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">Card Management</h1>
      
      {/* Lost Card Confirmation Dialog */}
      {showLostCardConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Card as Lost</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to report card <strong>{cardToReportLost?.cardNumber}</strong> as lost?
              This will deactivate the card and you'll need to add a replacement card.
            </p>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <strong>Warning:</strong> Please make sure the card is actually lost before reporting it. 
                    This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowLostCardConfirm(false)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReportLost}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700"
              >
                Confirm as Lost
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Student Selection */}
      <div className="bg-white shadow rounded-lg p-4 md:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <h2 className="text-lg font-medium text-gray-900">Select User</h2>
          <button
            onClick={() => setShowAddStudentForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
          >
            + Add New User
          </button>
        </div>

        {showAddStudentForm && (
          <div className="bg-blue-50 p-4 rounded-md mb-4">
            <h3 className="font-medium text-gray-900 mb-3">Add New User</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div>
                <label htmlFor="newStudentName" className="block text-sm font-medium text-gray-700 mb-1">
                  User Name
                </label>
                <input
                  type="text"
                  id="newStudentName"
                  value={newStudentName}
                  onChange={(e) => setNewStudentName(e.target.value)}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                  placeholder="Enter user name"
                />
              </div>
              <div>
                <label htmlFor="newStudentId" className="block text-sm font-medium text-gray-700 mb-1">
                  User Unique ID
                </label>
                <input
                  type="text"
                  id="newStudentId"
                  value={newStudentId}
                  onChange={(e) => setNewStudentId(e.target.value)}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                  placeholder="Enter user ID"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAddStudent}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                Save User
              </button>
              <button
                onClick={() => setShowAddStudentForm(false)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <select
          value={selectedStudent}
          onChange={(e) => handleStudentChange(Number(e.target.value))}
          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border mb-4"
          disabled={students.length === 0}
        >
          {students.length === 0 ? (
            <option value={0}>No users available</option>
          ) : (
            students.map(student => (
              <option key={student.id} value={student.id}>
                {student.name} ({student.studentId})
              </option>
            ))
          )}
        </select>

        {selectedStudentData && (
          <div className="bg-gray-50 p-4 rounded-md">
            <h3 className="font-medium text-gray-900">{selectedStudentData.name}</h3>
            <p className="text-sm text-gray-600">User ID: {selectedStudentData.studentId}</p>
            <p className="text-lg font-semibold text-green-600">
              Balance: PKR {selectedStudentData.balance.toFixed(2)}
            </p>
            {currentCard ? (
              <p className="text-sm text-gray-600">
                Current Card: {currentCard.cardNumber}
                {currentCard.isLost && (
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                    Lost
                  </span>
                )}
              </p>
            ) : (
              <p className="text-sm text-red-600">No active card</p>
            )}
          </div>
        )}
      </div>

      {/* Add/Replace Card Form */}
      {selectedStudentData && (!hasActiveCards || autoFocusNewCard) && (
        <div className="bg-white shadow rounded-lg p-4 md:p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            {currentCard?.isLost ? 'Replace Lost Card' : 'Add New Card'}
          </h2>
          
          {currentCard?.isLost && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    <strong>Card Reported Lost:</strong> Please add a replacement card to restore access for {selectedStudentData.name}.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="md:col-span-2">
              <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700 mb-1">
                RFID Card Number
              </label>
              <input
                type="text"
                id="cardNumber"
                value={newCardNumber}
                onChange={(e) => handleCardNumberChange(e.target.value)}
                className={`shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${
                  cardNumberError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                }`}
                placeholder="Enter 10-digit card number"
                autoFocus={autoFocusNewCard}
                maxLength={10}
              />
              {cardNumberError && (
                <p className="mt-1 text-sm text-red-600">{cardNumberError}</p>
              )}
              {newCardNumber.length > 0 && !cardNumberError && (
                <p className="mt-1 text-sm text-green-600">
                  {10 - newCardNumber.length} digits remaining
                </p>
              )}
            </div>
            <div className="mt-3 bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
              <p className="text-sm text-yellow-700">
                ⚠️ Note: A <strong>one-time activation fee of PKR 200</strong> will be
                auto-deducted from the first top-up on this card.
              </p>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => currentCard?.isLost ? handleReplaceCard(currentCard.id) : handleAddCard()}
                disabled={!newCardNumber || !!cardNumberError}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {currentCard?.isLost ? 'Replace Card' : 'Add Card'}
              </button>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Adding a new card will {currentCard?.isLost ? 'transfer the existing balance' : 'provide an initial balance'} to the new card.
          </p>
        </div>
      )}

      {/* Student's Cards */}
      {selectedStudentData && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              {selectedStudentData.name}'s Cards
            </h2>
            {refreshing && (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            )}
          </div>
          
          {studentCards.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No cards registered for this user
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {studentCards.map((card) => (
                <li key={card.id} className="p-4 md:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex-1 mb-4 sm:mb-0">
                      <p className="text-sm font-medium text-gray-900">{card.cardNumber}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          card.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {card.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {card.isLost && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Lost
                          </span>
                        )}
                        {card.id === currentCard?.id && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Current
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {card.isActive && !card.isLost && (
                        <button
                          onClick={() => confirmReportLost(card)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700"
                        >
                          Report Lost
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default CardManagement;